<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="720px"
    :fullscreen="$vuetify.display.smAndDown"
  >
    <v-card elevation="0">
      <v-container>
        <v-row>
          <!-- CAMERA SCANNER -->
          <v-col v-if="!physicalScanner" cols="12">
            <qrcode-stream
              v-if="dialog"
              :key="cameraFacing"
              :camera="cameraFacing"
              :paused="paused"
              class="vehicle-qr-stream"
              @decode="decode"
              @init="onInit"
            >
              <template #loading>
                <v-progress-circular indeterminate color="primary" />
              </template>
              <template #error="err">
                <v-alert type="error" variant="tonal">
                  {{ err?.message || $t("vehicle_entry.qr_scanner.physical_scanner_error") }}
                </v-alert>
              </template>
            </qrcode-stream>
          </v-col>

          <!-- PHYSICAL SCANNER -->
          <v-col v-else cols="12">
            <v-row class="my-2" justify="center">
              <div
                v-if="found === null && !physicalScannerError"
                class="ma-3 text-blue text-subtitle-1"
              >
                {{ $t("vehicle_entry.qr_scanner.physical_scanner") }}
              </div>
              <div
                v-else-if="physicalScannerError"
                class="ma-3 text-red text-subtitle-1"
              >
                {{ $t("vehicle_entry.qr_scanner.physical_scanner_error") }}
              </div>
            </v-row>
          </v-col>

          <!-- ALERTS & LOADER -->
          <v-col cols="12" class="text-center">
            <v-progress-circular v-if="loading" indeterminate color="primary" />
          </v-col>

          <v-col cols="12">
            <v-alert
              v-if="alert.value"
              :type="alert.type"
              variant="tonal"
              border="bottom"
            >
              {{ alert.message }}
            </v-alert>
          </v-col>

          <!-- ACTIONS (WHEN FOUND) -->
          <v-col v-if="found === true" cols="12" >
            <div v-if="mode === 'car'" class="vehicle-qr-actions">
              <v-btn
                variant="outlined"
                color="grey-darken-2"
                class="vehicle-qr-action-btn"
                @click="details"
              >
                {{ $t("vehicle_entry.qr_scanner.view_details") }}
                <v-icon end> mdi-view-list </v-icon>
              </v-btn>

              <v-btn
                variant="outlined"
                color="grey-darken-2"
                class="vehicle-qr-action-btn"
                @click="generateShipments"
              >
                {{ $t("vehicle_entry.qr_scanner.generate_shipments") }}
                <v-icon end> mdi-qrcode </v-icon>
              </v-btn>

              <v-btn
                v-if="
                  $store.getters.role == 'LekoAdmin' ||
                  $store.getters.role == 'InstitutionAdmin' ||
                  $store.getters.role == 'SuperAdmin'
                "
                variant="outlined"
                color="grey-darken-2"
                class="vehicle-qr-action-btn"
                @click="edit"
              >
                {{ $t("vehicle_entry.qr_scanner.edit_car") }}
                <v-icon end> mdi-pencil </v-icon>
              </v-btn>

              <v-btn
                variant="outlined"
                color="grey-darken-2"
                class="vehicle-qr-action-btn"
                @click="openLocationScanner"
              >
                {{ $t("vehicle_entry.qr_scanner.open_location_scanner") }}
                <v-icon end> mdi-map-marker </v-icon>
              </v-btn>
            </div>

            <div v-else-if="mode === 'location'" class="vehicle-qr-actions">
              <v-btn
                variant="outlined"
                color="grey-darken-2"
                class="vehicle-qr-action-btn"
                :loading="loadingChangeLocation"
                @click="changeLocation"
              >
                {{ $t("vehicle_entry.qr_scanner.change_location") }}
                <v-icon end> mdi-pencil </v-icon>
              </v-btn>
            </div>
          </v-col>

          <!-- BOTTOM ACTION BUTTONS -->
          <v-col cols="12">
            <v-row class="pa-2 align-center">
              <v-btn icon variant="text" @click="reScan">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>

              <v-spacer />

              <v-btn icon variant="text" @click="switchScanner">
                <v-icon>mdi-qrcode</v-icon>
              </v-btn>

              <v-btn
                v-if="!physicalScanner"
                icon
                variant="text"
                @click="switchCamera"
              >
                <v-icon>mdi-camera-switch</v-icon>
              </v-btn>

              <v-btn variant="outlined" color="error" @click="close">
                {{ $t("vehicle_entry.qr_scanner.close") }}
              </v-btn>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>
import getRequest from "../../api/getRequest";
import putRequest from "../../api/putRequest";
import { QrcodeStream } from "vue3-qrcode-reader";

export default {
  components: {
    QrcodeStream,
  },
  data: () => ({
    dialog: false,
    /** vue3-qrcode-reader accepts only: auto | front | rear | off */
    cameraFacing: "auto",
    paused: false,
    loading: false,
    loadingChangeLocation: false,
    mode: "car", // 'car' | 'location'
    alert: {
      value: false,
      type: "success",
      message: "",
    },
    car: {},
    location: {},
    found: null,
    decodedString: null,
    physicalScanner: false,
    physicalScannerError: false,
    buffer: "",
  }),
  methods: {
    async onInit(promise) {
      try {
        await promise;
        if (this.alert.type === "error" && !this.physicalScanner) {
          this.alert.value = false;
        }
      } catch (err) {
        if (err.name === "NotAllowedError") {
          this.alert = {
            value: true,
            type: "error",
            message: this.$t("vehicle_entry.qr_scanner.physical_scanner"),
          };
          this.switchScanner();
        } else if (
          (err.name === "OverconstrainedError" ||
            err.name === "NotFoundError") &&
          this.cameraFacing !== "auto"
        ) {
          this.cameraFacing = "auto";
        } else {
          this.alert = {
            value: true,
            type: "error",
            message: this.$t("vehicle_entry.qr_scanner.physical_scanner_error"),
          };
        }
      }
    },
    switchCamera() {
      this.physicalScanner = false;
      document.removeEventListener("keydown", this.listenToEvent);
      this.cameraFacing = this.cameraFacing === "front" ? "rear" : "front";
    },
    switchScanner() {
      this.physicalScanner = true;
      document.addEventListener("keydown", this.listenToEvent);
    },
    listenToEvent(e) {
      if (e.key !== "Enter") {
        this.buffer += e.key;
      } else if (this.buffer.length > 24) {
        this.physicalScannerError = true;
        this.buffer = "";
      } else {
        this.decode(this.buffer);
        this.buffer = "";
      }
    },
    async decode(value) {
      if (value !== null && value.length > 0) {
        this.loading = true;
        this.found = null;
        this.decodedString = value;
        this.paused = true;
        this.alert.value = false;

        if (this.mode === "car") {
          try {
            const response = await getRequest("/car", `id=${value}`);
            this.car = response.car;
            this.found = true;
            this.alert = {
              value: true,
              type: "success",
              message: `${this.$t("vehicle_entry.qr_scanner.found_message_1")} ${this.$t("vehicle_entry.qr_scanner.car")} ${this.$t("vehicle_entry.qr_scanner.message_2")}: ${this.decodedString}`,
            };
          } catch (error) {
            this.car = {};
            this.found = false;
            this.alert = {
              value: true,
              type: "error",
              message: `${this.$t("vehicle_entry.qr_scanner.not_found_message_1")} ${this.$t("vehicle_entry.qr_scanner.car")} ${this.$t("vehicle_entry.qr_scanner.message_2")}: ${this.decodedString}`,
            };
          }
        }

        if (this.mode === "location") {
          try {
            const response = await getRequest("/car-location", `id=${value}`);
            this.location = response.carLocations;
            this.found = true;
            this.alert = {
              value: true,
              type: "success",
              message: `${this.$t("vehicle_entry.qr_scanner.found_message_1")} ${this.$t("vehicle_entry.qr_scanner.location")} ${this.$t("vehicle_entry.qr_scanner.message_2")}: ${this.decodedString}`,
            };
          } catch (error) {
            this.location = {};
            this.found = false;
            this.alert = {
              value: true,
              type: "error",
              message: `${this.$t("vehicle_entry.qr_scanner.not_found_message_1")} ${this.$t("vehicle_entry.qr_scanner.location")} ${this.$t("vehicle_entry.qr_scanner.message_2")}: ${this.decodedString}`,
            };
          }
        }

        this.loading = false;
      }
    },
    reScan() {
      this.found = null;
      this.loading = false;
      this.paused = false;
      this.decodedString = null;
      this.physicalScannerError = false;
      if (this.mode !== "location") {
        this.car = {};
        this.location = {};
      }
      this.alert = { value: true, type: "info", message: "" };
      if (this.mode === "car") {
        this.alert.message = this.$t(
          "vehicle_entry.qr_scanner.scan_car_message",
        );
      }
      if (this.mode === "location") {
        this.alert.message = this.$t(
          "vehicle_entry.qr_scanner.scan_location_message",
        );
      }
    },
    openLocationScanner() {
      this.mode = "location";
      this.reScan();
    },
    changeLocation() {
      this.loadingChangeLocation = true;
      putRequest("/car/change-location", `id=${this.car._id}`, {
        location: this.location._id,
      })
        .then(() => {
          this.$emit("changed-location");
        })
        .catch(() => {})
        .finally(() => {
          this.loadingChangeLocation = false;
          this.close();
        });
    },
    details() {
      this.$emit("details", this.car);
      this.close();
    },
    generateShipments() {
      this.$emit("generate-qr", this.car);
      this.close();
    },
    edit() {
      this.$emit("edit", this.car);
      this.close();
    },
    async open() {
      this.cameraFacing = "auto";
      this.physicalScanner = false;
      this.paused = false;
      this.dialog = true;
      this.alert = {
        value: true,
        type: "info",
        message: this.$t("vehicle_entry.qr_scanner.scan_car_message"),
      };
    },
    close() {
      this.dialog = false;
      this.paused = false;
      this.loading = false;
      this.car = {};
      this.location = {};
      this.found = null;
      this.decodedString = null;
      this.physicalScanner = false;
      this.physicalScannerError = false;
      this.mode = "car";
      this.cameraFacing = "auto";
      this.alert = {
        value: false,
        type: "success",
        message: "",
      };
      document.removeEventListener("keydown", this.listenToEvent);
    },
  },
};
</script>

<style scoped>
.vehicle-qr-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.vehicle-qr-action-btn {
  width: 100%;
  height: auto !important;
  min-height: 44px;
  padding-top: 10px;
  padding-bottom: 10px;
}

.vehicle-qr-action-btn :deep(.v-btn__content) {
  width: 100%;
  white-space: normal;
  flex-wrap: wrap;
  justify-content: center;
  line-height: 1.35;
  gap: 6px;
  text-align: center;
}

.vehicle-qr-stream {
  display: block;
  width: 100%;
  max-height: 55vh;
}

.vehicle-qr-stream :deep(video) {
  width: 100%;
  max-height: 55vh;
  object-fit: cover;
}

@media (min-width: 600px) {
  .vehicle-qr-actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .vehicle-qr-action-btn {
    min-width: 0;
  }
}

@media (max-width: 959.98px) {
  .vehicle-qr-stream,
  .vehicle-qr-stream :deep(video) {
    max-height: calc(100dvh - 280px);
  }
}
</style>
